/**
 * ARIA follow-up draft API — collection email drafts (never auto-sends).
 */

import { getToken, resolveApiUrl } from './client'

export interface FollowUpDraftFacts {
  pay_app_id: number
  app_number: number
  project_id: number
  project_name: string
  owner_name: string
  owner_email: string | null
  company_name: string
  amount_due: number
  amount_due_formatted: string
  days_overdue: number
  payment_terms: string
  payment_link: string | null
  is_retainage_release: boolean
  lien_context?: {
    notice_type: string
    days_remaining: number
  } | null
}

export interface AriaBillingStatus {
  mode: 'byo' | 'app' | 'none'
  is_pro: boolean
  included_monthly: number
  used_monthly: number
  remaining_monthly: number | null
  daily_cap: number
  daily_used: number
  claude_oauth_configured: boolean
}

export interface FollowUpDraft {
  subject: string
  body_html: string
  lien_paragraph_html: string | null
  facts: FollowUpDraftFacts
  cached?: boolean
  billing?: AriaBillingStatus
}

interface FollowUpDraftResponse {
  data: FollowUpDraft
  message?: string
  error?: string
}

const DRAFT_TIMEOUT_MS = 20_000

function draftErrorMessage(status: number, body: FollowUpDraftResponse | Record<string, unknown>): string {
  const msg =
    (body as { message?: string }).message
    ?? (body as { error?: string }).error
  if (typeof msg === 'string' && msg.trim()) return msg
  return `Draft request failed (HTTP ${status})`
}

/** POST /api/aria/follow-up-drafts/:payAppId — AI wording only; facts from DB. */
export async function fetchFollowUpDraft(payAppId: number): Promise<FollowUpDraft> {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), DRAFT_TIMEOUT_MS)

  try {
    const token = getToken()
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`

    const res = await fetch(resolveApiUrl(`/api/aria/follow-up-drafts/${payAppId}`), {
      method: 'POST',
      headers,
      signal: controller.signal,
    })

    const body = await res.json().catch(() => ({})) as FollowUpDraftResponse

    if (!res.ok) {
      throw new Error(draftErrorMessage(res.status, body))
    }

    if (!body.data?.subject || !body.data?.body_html) {
      throw new Error('Draft response missing subject or body.')
    }

    return body.data
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('Draft request timed out after 20 seconds. Try again.')
    }
    if (err instanceof Error) throw err
    throw new Error('Draft request failed.')
  } finally {
    window.clearTimeout(timer)
  }
}

/** PATCH /api/aria/follow-up-recipient/:projectId */
export async function saveFollowUpRecipient(
  projectId: number,
  input: { email: string; name?: string | null },
): Promise<void> {
  const token = getToken()
  const res = await fetch(resolveApiUrl(`/api/aria/follow-up-recipient/${projectId}`), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string; message?: string }
    throw new Error(body.message || body.error || `Save failed (${res.status})`)
  }
}
